import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Button, ButtonToggle } from '@peacecover/component';
import { ArrowLeft, Attention } from '@peacecover/icon';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { useGetPaymentList } from '@pocapan/shared/domains/payments/ui/hooks/useGetPaymentList';
import { useGetMySubscription } from '@pocapan/shared/domains/payments/ui/hooks/useGetMySubscription';
import { useGetMyBettieAutoTopUp } from '@pocapan/shared/domains/bettie/ui/hooks/useGetMyBettieAutoTopup';
import { usePauseBettieAutoTopUp } from '@pocapan/shared/domains/bettie/ui/hooks/usePauseBettieAutoTopUp';
import { useActivateBettieAutoTopUp } from '@pocapan/shared/domains/bettie/ui/hooks/useActivateBettieAutoTopup';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { useRetryChargeAutoTopUp } from './hooks/useRetryChargeAutoTopUp';
import { useCancelSubscription } from '@pocapan/shared/domains/payments/ui/hooks/useCancelSubscription';
import { formatDateTime } from '@pocapan/shared/utils/DateUtil';
import RefundExchangeNotice from './components/RefundExchangeNotice';
import BettiePurchaseHistoryItem from './components/BettiePurchaseHistoryItem';
import EmptyCaseWithTicketImage from './components/EmptyCaseWithTicketImage';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import type { components } from '@pocapan/shared/types/schema';

type MySubScriptionType = components['schemas']['ApiV1Subscription'];
type BettieAutoTopUpType = components['schemas']['ApiV1BettieAutoTopup'];

const PAY_LETTER_REGISTRATION_ONLY_PRODUCT_ID =
  import.meta.env.REACT_APP_DEPLOY_ENV === 'production'
    ? '26773035-29c4-46a2-8fa8-eba7a76a8e3d'
    : 'ad6c8565-b432-47f8-8de0-0fe3a1db12b9';
const PAY_LETTER_REGISTRATION_ONLY_PRODUCT_NAME = '자동결제 결제수단 변경';

const BettiePurchaseHistoryPage = () => {
  const navigate = useNavigate();
  const handleHistoryBack = useHandleHistoryBack();
  const { data: payments, isLoading } = useGetPaymentList();
  const { data: mySubscription, isLoading: isMySubscriptionLoading } = useGetMySubscription();
  const { data: bettieAutoTopUp, isLoading: isBettieAutoTopUpLoading } = useGetMyBettieAutoTopUp();
  const { mutateAsync: cancelSubscription, isLoading: isCancelingSubscription } =
    useCancelSubscription();

  const [isChargeBottomSheetOpen, setIsChargeBottomSheetOpen] = useState(false);

  const handleChangeCardClick = () => {
    openNewTab(
      `/payment/pay-letter?amount=0&productName=${encodeURIComponent(PAY_LETTER_REGISTRATION_ONLY_PRODUCT_NAME)}&productId=${PAY_LETTER_REGISTRATION_ONLY_PRODUCT_ID}&autoPay=true&registrationOnly=true`
    );
  };

  const handleCardClick = async () => {
    const subscriptionId = mySubscription?.subscriptionId;
    if (!subscriptionId || isCancelingSubscription) return;

    try {
      const response = await cancelSubscription({ subscriptionId });
      if (response?.subscriptionId) {
        const cancelDate = new Date(response.startedAt);
        cancelDate.setFullYear(cancelDate.getFullYear() + 1);

        navigate(
          `/subscription/${subscriptionId}/cancel-success?cancel_date=${cancelDate.toISOString()}`,
          { replace: true }
        );
      } else {
        throw new Error('Failed to cancel subscription');
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+32px)]">
      <div className="sticky top-0 flex items-center justify-center px-[16px] py-[10px] bg-background-light dark:bg-background-dark">
        <ArrowLeft
          size={24}
          className="text-text-main-lv1 absolute left-[16px]"
          onClick={handleHistoryBack}
        />
        <p className="col-start-2 text-[17px] font-bold text-text-main-lv1 leading-[20px] text-center">
          {t('BettiePurchaseHistory.title')}
        </p>
      </div>
      {bettieAutoTopUp && !isBettieAutoTopUpLoading && (
        <BettieAutoTopUpCard
          bettieAutoTopUp={bettieAutoTopUp}
          onEdit={() => setIsChargeBottomSheetOpen(true)}
          onChangeCard={handleChangeCardClick}
          hasBottomPadding={payments?.length === 0 || isMySubscriptionLoading || !mySubscription}
        />
      )}
      {payments?.length === 0 || isMySubscriptionLoading || !mySubscription ? null : (
        <MySubscriptionCard
          mySubscription={mySubscription}
          onClick={handleCardClick}
          isLoading={isCancelingSubscription}
        />
      )}
      <div className="flex flex-col items-center px-[24px] mt-[28px]">
        {isLoading ? (
          <>
            <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px] flex justify-start w-full mb-[20px]">
              {t('BettiePurchaseHistory.purchaseHistory')}
            </p>
            <div className="w-full flex flex-col gap-[10px]">
              <BettiePurchaseCardSkeleton />
            </div>
          </>
        ) : payments?.length === 0 ? (
          <EmptyCaseWithTicketImage
            title={t('BettiePurchaseHistory.emptyCaseTitle')}
            className="mt-[196px]"
          />
        ) : (
          <>
            <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px] flex justify-start w-full mb-[20px]">
              {t('BettiePurchaseHistory.purchaseHistory')}
            </p>
            <div className="w-full flex flex-col gap-[10px]">
              {payments?.map((item) => (
                <BettiePurchaseHistoryItem key={item.paymentId} item={item} />
              ))}
            </div>
          </>
        )}
        <RefundExchangeNotice className={payments?.length === 0 ? 'mt-[300px]' : 'mt-[100px]'} />
      </div>
      <BettieChargeBottomSheet
        isOpen={isChargeBottomSheetOpen}
        onClose={() => setIsChargeBottomSheetOpen(false)}
      />
    </div>
  );
};

export default BettiePurchaseHistoryPage;

const BettiePurchaseCardSkeleton = () => {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: skelton key
          key={index}
          className="w-full px-[20px] py-[16px] bg-background-elevated rounded-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.06),_0_0_12px_0_rgba(0,0,0,0.08)] animate-pulse"
        >
          <div className="w-full flex flex-col gap-[15px] items-start justify-between">
            <div className="h-[14px] w-[80px] rounded-full bg-gray-100 dark:bg-gray-800" />
            <div className="flex justify-between items-center w-full">
              <div className="flex flex-col items-start gap-[6px] min-w-0 flex-1">
                <div className="h-[18px] w-[140px] rounded-full bg-gray-100 dark:bg-gray-800" />
                <div className="h-[16px] w-[180px] rounded-full bg-gray-100 dark:bg-gray-800" />
              </div>
              <div className="h-[19px] w-[60px] rounded-full bg-gray-100 dark:bg-gray-800 flex-shrink-0" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

const BettieAutoTopUpCard = ({
  bettieAutoTopUp,
  onEdit,
  onChangeCard,
  hasBottomPadding
}: {
  bettieAutoTopUp: BettieAutoTopUpType;
  onEdit: () => void;
  onChangeCard: () => void;
  hasBottomPadding: boolean;
}) => {
  const { mutateAsync: pauseAutoTopup, isLoading: isPausing } = usePauseBettieAutoTopUp();
  const { mutateAsync: activateAutoTopup, isLoading: isActivating } = useActivateBettieAutoTopUp();
  const { mutateAsync: retryChargeAutoTopup, isLoading: isRetrying } = useRetryChargeAutoTopUp();

  const handleToggle = async () => {
    if (isPausing || isActivating) return;
    const id = bettieAutoTopUp.autoTopupId;
    try {
      if (bettieAutoTopUp.status === 'active') {
        await pauseAutoTopup(id);
      } else if (bettieAutoTopUp.status === 'paused') {
        await activateAutoTopup(id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRetryCharge = async () => {
    if (bettieAutoTopUp.status !== 'payment_failed' || isRetrying) return;
    try {
      await retryChargeAutoTopup(bettieAutoTopUp.autoTopupId);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleBusy = isPausing || isActivating;
  const canToggle = bettieAutoTopUp.status === 'active' || bettieAutoTopUp.status === 'paused';
  const paymentFailed = bettieAutoTopUp.status === 'payment_failed';

  return (
    <div className={`bg-surface-lv2 px-[24px] pt-[20px] ${hasBottomPadding ? 'pb-[20px]' : ''}`}>
      <div className="flex items-center justify-between">
        <p className="text-[15px] font-semibold text-text-main-lv2">
          {t('BettiePurchaseHistory.autoTopup')}{' '}
          {bettieAutoTopUp.status === 'active' && (
            <span className="text-primary-light dark:text-primary-dark text-[14px] font-bold">
              {t('BettiePurchaseHistory.active')}
            </span>
          )}
          {bettieAutoTopUp.status === 'paused' && (
            <span className="text-[#FF495A] text-[14px] font-bold">
              {t('BettiePurchaseHistory.paused')}
            </span>
          )}
        </p>
      </div>
      <div className="rounded-[20px] p-[1px] bg-[linear-gradient(102deg,#860EFF_0%,#2DE7D1_44%,#C855FF_100%)] mt-[18px] shadow-[0_0_0_1px_rgba(0,0,0,0.01),0_0_14px_0_rgba(0,0,0,0.08)]">
        <div className="relative rounded-[19px] bg-white dark:bg-black px-[20px] pt-[16px] pb-[18px] flex flex-col items-start gap-[7px]">
          <p className="text-[12px] text-text-main-lv2 leading-[14px]">
            {t('BettiePurchaseHistory.chargeThreshold', {
              count: bettieAutoTopUp.threshold
            })}
          </p>
          <p className="text-[17px] font-semibold text-text-main-lv2 leading-[20px]">
            {t('BettiePurchaseHistory.chargeQuantity', {
              count: bettieAutoTopUp.pointQuantity
            })}
          </p>
          <p className="text-[12px] text-text-main-lv4 leading-[14px]">
            {t('BettiePurchaseHistory.registerDate', {
              date: formatDateTime(bettieAutoTopUp.registeredAt, 'yyyy.MM.dd')
            })}
            {' · '}
            {t('BettiePurchaseHistory.currentCard', {
              cardNumber: bettieAutoTopUp.maskedCardNumber?.slice(-4) ?? '-'
            })}
          </p>
          <div className="flex items-center gap-[10px] w-full mt-[3px]">
            {paymentFailed ? (
              <button
                type="button"
                onClick={handleRetryCharge}
                className="text-[14px] font-semibold leading-[17px] text-on-secondary bg-secondary rounded-[12px] flex-1 py-[8.5px]"
              >
                {t('BettiePurchaseHistory.retry')}
              </button>
            ) : (
              <button
                type="button"
                className="text-[14px] font-semibold leading-[17px] text-on-secondary bg-secondary rounded-[12px] flex-1 py-[8.5px]"
                onClick={onEdit}
              >
                {t('BettiePurchaseHistory.changeProduct')}
              </button>
            )}
            <button
              type="button"
              className="text-[14px] font-semibold leading-[17px] text-on-warning bg-warningContainer rounded-[12px] flex-1 py-[8.5px]"
              onClick={onChangeCard}
            >
              {t('BettiePurchaseHistory.changeCard')}
            </button>
          </div>
          <ButtonToggle
            size="lg"
            type="switch"
            checked={bettieAutoTopUp.status === 'active'}
            disabled={!canToggle || toggleBusy || paymentFailed}
            className={`absolute top-[26px] right-[21px] ${paymentFailed ? 'opacity-30' : ''}`}
            onChange={handleToggle}
          />
        </div>
      </div>
      {paymentFailed && (
        <div className="flex gap-[4px] items-center justify-center mt-[10px] bg-warningContainer rounded-[7px] py-[3px]">
          <Attention size={14} className="text-red-400" />
          <span className="text-[12px] font-medium text-red-400 leading-[14px]">
            {t('BettiePurchaseHistory.paymentFailed')}
          </span>
        </div>
      )}
    </div>
  );
};

const MySubscriptionCard = ({
  mySubscription,
  onClick,
  isLoading
}: {
  mySubscription: MySubScriptionType;
  onClick: () => void;
  isLoading: boolean;
}) => {
  const nextBillingDay = mySubscription.nextBillingDate
    ? new Date(mySubscription.nextBillingDate).getDate()
    : undefined;

  const [title, description] = mySubscription.productName.split('/');

  return (
    <div className="bg-surface-lv2 px-[24px] pt-[24px] pb-[20px]">
      <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
        {t('BettiePurchaseHistory.mySubscription')}
      </p>
      <div className="flex flex-col gap-[15px] px-[20px] py-[16px] bg-background-elevated rounded-[20px] shadow-[0_0_3px_0_rgba(0,0,0,0.06),_0_0_12px_0_rgba(0,0,0,0.08)] mt-[20px]">
        <p className="text-[12px] text-text-main-lv2 leading-[14px]">
          {formatDateTime(mySubscription?.startedAt, 'PPP')}
        </p>
        <div className="flex flex-col gap-[6px]">
          <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
            {title.trim()}
            {description && (
              <span className="text-[13px] font-normal ml-[4px]">{description.trim()}</span>
            )}
          </p>
          <p className="text-[12px] text-text-main-lv4 leading-[14px]">
            {t('BettiePurchaseHistory.billingDetail', {
              price: `${Number(mySubscription?.amount).toLocaleString()}원`,
              date: nextBillingDay ?? '-'
            })}
          </p>
        </div>
        <Button
          size="regular"
          btnColor="secondary"
          className="w-full text-[14px] font-semibold leading-[17px] rounded-[12px] bg-[#FFE7EA] text-on-warning"
          disabled={isLoading}
          onClick={onClick}
        >
          {t('BettiePurchaseHistory.cancelRequest')}
        </Button>
      </div>
    </div>
  );
};
